import { useState, useEffect } from "react";
import useReveal from "../hooks/useReveal";
import "./PrivacyPolicy.css";

const SECTIONS = [
  {
    id: "s1",
    title: "1. Information We Collect",
    body: `We collect information you provide directly to us when you fill out our contact form, request a quote, subscribe to our newsletter, or engage with our services. This includes your name, email address, phone number, company name, and project details.

We also automatically collect certain technical information when you visit our website, including your IP address, browser type and version, operating system, referring URLs, pages visited, and time spent on each page. This data is collected via cookies and similar tracking technologies.`,
  },
  {
    id: "s2",
    title: "2. How We Use Your Information",
    body: `We use the information we collect to respond to your enquiries and fulfil your requests; send administrative updates and project communications; deliver relevant marketing content where you have given consent; improve our website performance and user experience; analyse traffic patterns and optimise our services; comply with applicable legal obligations; and protect our rights and prevent misuse of our platform.

We never sell, rent, or trade your personal data to third parties for their marketing purposes.`,
  },
  {
    id: "s3",
    title: "3. Information Sharing",
    body: `We do not sell or rent your personal information to any third party. We may share your information with carefully selected service providers who assist us in delivering our services - such as hosting providers, analytics tools, and email platforms - under strict confidentiality agreements.

We may also disclose your information where required by law, court order, or regulatory authority, or to protect the rights, property, or safety of CubeX Technology, our clients, or others.`,
  },
  {
    id: "s4",
    title: "4. Cookies & Tracking Technologies",
    body: `Our website uses cookies and similar tracking technologies to enhance your browsing experience, analyse site traffic, and understand visitor behaviour. Cookies we use include essential cookies (required for the site to function), analytics cookies (to understand how visitors interact with our site), and preference cookies (to remember your settings).

You can control cookie settings through your browser preferences at any time. Note that disabling certain cookies may affect the functionality of parts of our website.`,
  },
  {
    id: "s5",
    title: "5. Data Security",
    body: `We implement industry-standard security measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. These include SSL/TLS encryption for data in transit, secure server infrastructure, access controls and authentication systems, and regular security audits.

However, no method of transmission over the internet or electronic storage is completely secure. While we strive to protect your data, we cannot guarantee absolute security.`,
  },
  {
    id: "s6",
    title: "6. Data Retention",
    body: `We retain your personal information for as long as necessary to fulfil the purposes outlined in this Privacy Policy unless a longer retention period is required or permitted by law. Project-related data is typically retained for the duration of our working relationship plus five years for accounting and legal compliance purposes.

When your information is no longer needed, we securely delete or anonymise it in accordance with our data retention schedule.`,
  },
  {
    id: "s7",
    title: "7. Your Rights",
    body: `Depending on your location, you may have certain rights regarding your personal information. These may include the right to access the personal data we hold about you; the right to correct inaccurate or incomplete data; the right to request deletion of your data; the right to restrict or object to certain processing; the right to data portability; and the right to withdraw consent at any time.

To exercise any of these rights, please contact us at privacy@cubextech.com. We will respond to all legitimate requests within 30 days.`,
  },
  {
    id: "s8",
    title: "8. Third-Party Links",
    body: `Our website may contain links to third-party websites, social media platforms, and external resources. We are not responsible for the privacy practices, content, or security of these external sites. We encourage you to review the privacy policy of any third-party site you visit through links on our platform.`,
  },
  {
    id: "s9",
    title: "9. Children's Privacy",
    body: `Our services are intended for business professionals and are not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected information from someone under 18, please contact us immediately at privacy@cubextech.com and we will take prompt steps to delete such information from our systems.`,
  },
  {
    id: "s10",
    title: "10. Changes to This Policy",
    body: `We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or for other operational reasons. When we make material changes, we will update the effective date at the top of this page and may notify registered users by email.

We encourage you to review this policy periodically to stay informed about how we protect your information. Your continued use of our website after any changes constitutes acceptance of the updated policy.`,
  },
  {
    id: "s11",
    title: "11. Contact Us",
    body: `If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please reach out to us through any of the following channels:

CubeX Technology
Vadodara, Gujarat, India - 390021
Email: info@cubextechnology.com
Phone: +91 92651 89542, +91 94283 23600
Working hours: Monday to Saturday, 9 AM – 6 PM IST

We take privacy seriously and are committed to resolving any concerns promptly and transparently.`,
  },
];

export default function PrivacyPolicy() {
  useReveal();
  const [active, setActive] = useState("s1");

  useEffect(() => {
    const handler = () => {
      const sections = document.querySelectorAll(".pp-section");
      sections.forEach((s) => {
        const rect = s.getBoundingClientRect();
        if (rect.top <= 160 && rect.bottom >= 160) {
          setActive(s.id);
        }
      });
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="pp-page">
      {/* PAGE HERO */}
      <div className="page-hero">
        <span className="ph-eye">Legal</span>
        <h1 className="ph-title">
          Privacy <span className="g">Policy</span>
        </h1>
        <p className="ph-sub">
          Effective Date: January 1, 2025 &nbsp;·&nbsp; Last Updated: January
          15, 2025
        </p>
      </div>

      {/* CONTENT */}
      <section className="pp-sect">
        <div className="container">
          <div className="pp-layout">
            {/* Sticky TOC */}
            <aside className="pp-toc">
              <p className="pp-toc__label">Table of Contents</p>
              <ul className="pp-toc__list">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <button
                      className={`pp-toc__link${active === s.id ? " pp-toc__link--active" : ""}`}
                      onClick={() => scrollTo(s.id)}
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main content */}
            <div className="pp-content">
              {/* Intro card */}
              <div className="pp-intro reveal">
                <span className="pp-intro__icon">🔒</span>
                <p>
                  At <strong>CubeX Technology</strong>, we are committed to
                  protecting your privacy and ensuring the security of your
                  personal information. This Privacy Policy explains how we
                  collect, use, disclose, and safeguard your information when
                  you visit our website or use our services. Please read this
                  carefully.
                </p>
              </div>

              {/* Sections */}
              {SECTIONS.map((s) => (
                <div
                  key={s.id}
                  id={s.id}
                  className="pp-section reveal"
                  style={{ scrollMarginTop: "100px" }}
                >
                  <h2 className="pp-section__title">{s.title}</h2>
                  <div className="pp-section__body">
                    {s.body.split("\n\n").map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer note */}
              <div className="pp-footer-note reveal">
                <span>✦</span>
                <p>
                  By using the CubeX Technology website, you acknowledge that
                  you have read, understood, and agree to be bound by this
                  Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
